import Card from "components/Card";
import Page from "components/Page";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import moment from "moment";
import StaffAttendanceSidebar from "./components/StaffAttendanceSidebar";
import StaffAttendanceTable from "./components/StaffAttendanceTable";
import Button from "components/Button";
import CustomSortDropdown from "components/CustomSortDropdown";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  useGlobalTimesheetReport,
  updateTimesheetStatus,
  exportGlobalTimesheetReport,
} from "repositories/timesheet-repository";
import Loader from "components/Loader";
import FamilyModal from "patient-scheduling/pages/FamilyMembers/components/AddedMembers/FamilyModal";
import RejectionModal from "staff/pages/StaffListingTimesheet/component/RejectionModal";
import constants from "../../../constants";
import toast from "react-hot-toast";
import NotAccessible from "accountOwner/pages/ManagerDashboard/components/NotAccessible";
import {
  encodeId,
  getCurrentMonthToTodayDateRange,
  getStorage,
  handleError,
  isDateRangeEndAtOrAfterToday,
  normalizeStoredDateRange,
  parseStoredDateValue,
  serializeDateRangeForStorage,
  setStorage,
  shouldShowGoToCurrentDateButton,
} from "utils";

// import constants from "../../constants";
import styles from "./StaffAttendanceTable.module.scss";
import AddTimesheetStaffModal from "./modals/AddTimesheetStaffModal";

const StaffAttendance = ({ t, history }) => {
  const [checkedColumns, setCheckedColumns] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [search, setSearch] = useState("");

  // Handle search input
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Align with Timesheet Report: shared session cache + timezone-safe dates
  const loadDateRangeFromStorage = () => {
    const cacheKey = constants.notes.cache.timeSheetSelectedDateRange;

    const stored = getStorage(cacheKey);
    if (stored?.from != null && stored?.to != null) {
      return normalizeStoredDateRange({
        from: parseStoredDateValue(stored.from),
        to: parseStoredDateValue(stored.to),
      });
    }

    // One-time migration from legacy localStorage key
    try {
      const legacy = localStorage.getItem("staffAttendanceDateRange");
      if (legacy) {
        const parsed = JSON.parse(legacy);
        let range = normalizeStoredDateRange({
          from: parseStoredDateValue(parsed.from),
          to: parseStoredDateValue(parsed.to),
        });
        const today = moment().startOf("day");
        const fromM = moment(range.from).startOf("day");
        const toM = moment(range.to).startOf("day");
        const currentMonthStart = today.clone().startOf("month");

        if (fromM.isSame(currentMonthStart, "day") && toM.isBefore(today)) {
          range = { ...range, to: today.toDate() };
        }

        setStorage(cacheKey, serializeDateRangeForStorage(range));
        localStorage.removeItem("staffAttendanceDateRange");
        return range;
      }
    } catch (error) {
      console.error("Error loading legacy staff attendance date range:", error);
    }

    return getCurrentMonthToTodayDateRange();
  };

  const [dateRange, setDateRange] = useState(loadDateRangeFromStorage);

  useEffect(() => {
    setStorage(
      constants.notes.cache.timeSheetSelectedDateRange,
      serializeDateRangeForStorage(dateRange),
    );
  }, [dateRange]);

  const {
    selectedId,
    isOwner: isAccountOwner,
    isStaff,
    dataAccessValues,
    getCommonAccessLoading,
    ownerListLoading,
    commonOwnerData,
    profile,
  } = useAccountOwnerCommon();

  // Check access permissions
  const staffCanAccess = isStaff && dataAccessValues?.isAdmin;
  const canAccessThisPage = isAccountOwner || staffCanAccess;
  const canAddTimesheet = isAccountOwner || profile?.isAdmin;

  // Format dates for API call (YYYY-MM-DD format)
  const startDate = moment(dateRange.from).format("YYYY-MM-DD");
  const endDate = moment(dateRange.to).format("YYYY-MM-DD");

  // Sort options matching TimesheetReport
  const sortByList = [
    {
      id: 1,
      label: t("staffTimesheet.sortByAlphabetical"),
      value: "1",
      className: "creation-date-option",
    },
    {
      id: 2,
      label: t("staffTimesheet.sortByEmploymentDate"),
      value: "2",
      className: "due-date-option",
    },
  ];

  // Default to alphabetical sorting
  const [selectedSort, setSelectedSort] = useState(sortByList[0].value);

  const [showLoader, setShowLoader] = useState(false);
  const [bulkConfirmModal, setBulkConfirmModal] = useState({
    open: false,
    type: null,
  });
  const [bulkRejectionModal, setBulkRejectionModal] = useState(false);
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState([]);
  const timesheetListingStatus = constants.TimesheetListingStatus;

  // Filter states
  const [selectedOfficeIds, setSelectedOfficeIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedDesignationIds, setSelectedDesignationIds] = useState([]);
  const [selectedStaffStatus, setSelectedStaffStatus] = useState([
    "IsActiveInclude",
  ]);
  const [selectedTimesheetStatuses, setSelectedTimesheetStatuses] = useState(
    [],
  );
  const [isAddTimesheetModalOpen, setIsAddTimesheetModalOpen] = useState(false);

  // Prepare staff status values for API (convert array to boolean flags)
  const staffStatusValues = useMemo(() => {
    const value = {
      IsDeleteInclude: false,
      IsInActiveInclude: false,
      IsActiveInclude: false,
    };
    // If selectedStaffStatus is an array, set flags based on selected values
    if (Array.isArray(selectedStaffStatus)) {
      selectedStaffStatus.forEach((status) => {
        if (value.hasOwnProperty(status)) {
          value[status] = true;
        }
      });
    } else {
      // Backward compatibility: if it's still an object, use the value
      if (selectedStaffStatus?.value) {
        value[selectedStaffStatus.value] = true;
      } else {
        // Default to Active if nothing is selected
        value.IsActiveInclude = true;
      }
    }
    return value;
  }, [selectedStaffStatus]);

  const { data, isLoading, isFetching, refetch } = useGlobalTimesheetReport(
    selectedOfficeIds, // OfficeIds
    selectedId, // OwnerId
    startDate, // StartDate
    endDate, // EndDate
    selectedSort, // SortBy
    { enabled: !!selectedId && canAccessThisPage }, // options
    selectedUserIds, // UserIds
    selectedDesignationIds, // DesignationIds
    staffStatusValues.IsActiveInclude, // IsActiveInclude
    staffStatusValues.IsDeleteInclude, // IsDeleteInclude
    staffStatusValues.IsInActiveInclude, // IsInActiveInclude
  );

  // Process staff list and holidays from API response
  const [staffListTimesheet, setStaffListTimesheet] = useState([]);
  const [holidays, setHolidays] = useState(new Map());

  // Filter staff by timesheet status (FE only)
  // Shows staff that has at least one timesheet matching the selected status(es)
  const filterByTimesheetStatus = useCallback(
    (staff) => {
      if (
        !selectedTimesheetStatuses ||
        selectedTimesheetStatuses.length === 0
      ) {
        return true;
      }

      if (!staff?.timesheets || staff.timesheets.length === 0) {
        // If no timesheets, don't show
        return false;
      }

      // Get all statusIds from timesheets (including duplicates)
      // Convert to numbers for consistent comparison
      const statusIds = staff.timesheets
        .map((ts) => Number(ts.statusId))
        .filter((id) => !isNaN(id) && id !== null && id !== undefined);

      if (statusIds.length === 0) {
        return false;
      }

      // Get unique statusIds
      const uniqueStatusIds = [...new Set(statusIds)];

      // Map filter values to actual status values
      const statusValueMap = {
        pending: Number(timesheetListingStatus[1]?.value), // 2
        approved: Number(timesheetListingStatus[2]?.value), // 3
        rejected: Number(timesheetListingStatus[3]?.value), // 4
        paid: Number(timesheetListingStatus[4]?.value), // 5
      };

      // Get the status values for selected filters (excluding "mixed")
      const selectedStatusValues = selectedTimesheetStatuses
        .filter((status) => status !== "mixed")
        .map((status) => statusValueMap[status])
        .filter((val) => !isNaN(val) && val !== null && val !== undefined);

      // Check if "Mixed" is selected
      const isMixedSelected = selectedTimesheetStatuses.includes("mixed");
      const isMixed = uniqueStatusIds.length > 1;

      // If only "Mixed" is selected (no other statuses), only show records with multiple unique statuses
      if (isMixedSelected && selectedStatusValues.length === 0) {
        return isMixed;
      }

      // If "Mixed" is selected along with other statuses, show records that either:
      // 1. Have mixed statuses (multiple unique statuses), OR
      // 2. Have at least one timesheet matching the selected statuses
      if (isMixedSelected && selectedStatusValues.length > 0) {
        // Check if any timesheet matches selected statuses
        const hasMatchingStatus = uniqueStatusIds.some((statusId) =>
          selectedStatusValues.includes(statusId),
        );

        // Return true if either mixed OR has matching status
        return isMixed || hasMatchingStatus;
      }

      // If only specific statuses are selected (no "Mixed"), check if ANY timesheet matches
      if (selectedStatusValues.length === 0) {
        return false;
      }

      // Check if ANY timesheet has one of the selected statuses
      // This means: at least one statusId must be in the selectedStatusValues array
      const hasMatchingStatus = uniqueStatusIds.some((statusId) =>
        selectedStatusValues.includes(statusId),
      );

      return hasMatchingStatus;
    },
    [selectedTimesheetStatuses, timesheetListingStatus],
  );

  useEffect(() => {
    if (!isLoading && data?.data) {
      setHolidays(
        new Map(
          (data?.data?.holidays || []).map((holiday) => [
            moment(holiday.date).format("YYYY-MM-DD"),
            holiday,
          ]),
        ),
      );
      // Filter out the logged-in user from the staff list
      const loggedInUserId = profile?.id;
      let staffList = (data?.data?.staff || []).filter(
        (staff) => staff?.user?.id !== loggedInUserId,
      );

      // Apply timesheet status filter (FE only)
      staffList = staffList.filter(filterByTimesheetStatus);

      // Apply search filter
      if (search && search.trim() !== "") {
        const searchLower = search.toLowerCase().trim();
        staffList = staffList.filter((staff) => {
          const firstName = staff?.user?.firstName || "";
          const lastName = staff?.user?.lastName || "";
          const fullName = `${firstName} ${lastName}`.toLowerCase();
          return fullName.includes(searchLower);
        });
      }

      setStaffListTimesheet(staffList);
    }
  }, [isLoading, data, profile?.id, filterByTimesheetStatus, search]);

  const showGoToCurrentDate = useMemo(
    () => shouldShowGoToCurrentDateButton(dateRange.to),
    [dateRange.to],
  );

  const isAtCurrentPeriodEnd = useMemo(
    () => isDateRangeEndAtOrAfterToday(dateRange.to),
    [dateRange.to],
  );

  // Generate date headers from startDate to endDate
  const dateHeaders = useMemo(() => {
    const headers = [];
    let current = moment(startDate, "YYYY-MM-DD");
    const last = moment(endDate, "YYYY-MM-DD");

    while (current.isSameOrBefore(last, "day")) {
      headers.push({
        date: current.format("YYYY-MM-DD"),
        label: current.format("ddd DD, MMM"),
        weekday: current.format("ddd"),
        isHoliday: holidays.has(current.format("YYYY-MM-DD")),
      });
      current.add(1, "day");
    }
    return headers;
  }, [startDate, endDate, holidays]);

  // Toggle select all
  const handleSelectAllChange = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    // Update all non-holiday date columns
    const updatedColumns = {};
    dateHeaders.forEach((dateHeader) => {
      if (!dateHeader.isHoliday) updatedColumns[dateHeader.date] = newSelectAll;
    });
    setCheckedColumns(updatedColumns);
  };

  // Individual column toggle (triggered by table)
  const handleCheckboxChange = (date) => {
    setCheckedColumns((prev) => {
      const newState = { ...prev, [date]: !prev[date] };

      // Automatically update "select all" if all non-holiday columns are checked
      const allChecked = dateHeaders
        .filter((dh) => !dh.isHoliday)
        .every((dh) => newState[dh.date]);

      setSelectAll(allChecked);
      return newState;
    });
  };

  // Date range update handler with validation
  const updateDateRange = (date, type) => {
    setDateRange((prev) => {
      let newRange = { ...prev };
      const today = moment().startOf("day");

      if (type === "from") {
        const selectedFromDate = moment(date).startOf("day");
        const currentToDate = moment(prev.to);

        // Don't allow future dates
        if (selectedFromDate.isAfter(today)) {
          newRange.from = today.toDate();
        } else {
          newRange.from = date;
        }

        // Validate that the range doesn't exceed 1 month
        const fromDate = moment(newRange.from);
        const toDate = moment(newRange.to);
        const monthsDiff = toDate.diff(fromDate, "months", true);

        if (monthsDiff >= 1) {
          // If range exceeds 1 month, set end date to 1 month from start
          const maxEndDate = moment(fromDate)
            .add(1, "month")
            .subtract(1, "day");
          newRange.to = maxEndDate.isAfter(today)
            ? today.toDate()
            : maxEndDate.toDate();
        }
      } else if (type === "to") {
        const selectedToDate = moment(date).startOf("day");
        const fromDate = moment(prev.from).startOf("day");

        // Today button / explicit today selection always wins
        if (selectedToDate.isSame(today, "day")) {
          newRange.to = today.toDate();
          return newRange;
        }

        // Don't allow future dates
        if (selectedToDate.isAfter(today)) {
          newRange.to = today.toDate();
        } else if (selectedToDate.isBefore(fromDate)) {
          // Don't allow end date before start date
          return prev;
        } else {
          // Validate that the range doesn't exceed 1 month
          const monthsDiff = selectedToDate.diff(fromDate, "months", true);

          if (monthsDiff >= 1) {
            // If range exceeds 1 month, set end date to 1 month from start
            const maxEndDate = moment(fromDate)
              .add(1, "month")
              .subtract(1, "day");
            newRange.to = maxEndDate.isAfter(today)
              ? today.toDate()
              : maxEndDate.toDate();
          } else {
            newRange.to = selectedToDate.toDate();
          }
        }
      }

      return newRange;
    });
  };

  // Go to current date keeping the previous range length
  const goToCurrentDate = () => {
    setDateRange((prev) => {
      const daysDiff = moment(prev.to).diff(moment(prev.from), "days");
      const today = moment().startOf("day");

      const newToDate = moment.min(today, moment(today).add(daysDiff, "days"));
      const newFromDate = moment(newToDate).subtract(daysDiff, "days");

      return {
        from: newFromDate.toDate(),
        to: newToDate.toDate(),
      };
    });
  };

  // Move the date range forward by the current window size (without exceeding today)
  const handleNext = () => {
    // Reset Select All and checked columns when navigating
    setSelectAll(false);
    setCheckedColumns({});

    setDateRange((prev) => {
      const fromDate = moment(prev.from);
      const toDate = moment(prev.to);
      const currentDate = moment();

      const isFirstDay = fromDate.date() === 1;
      const isLastDay = toDate.isSame(moment(fromDate).endOf("month"), "day");

      if (isFirstDay && isLastDay) {
        const newFromDate = moment(fromDate).add(1, "month").startOf("month");

        if (newFromDate.isAfter(currentDate)) {
          return prev;
        }

        const newToDate = moment(newFromDate).endOf("month");
        return {
          from: newFromDate.toDate(),
          to: newToDate.isAfter(currentDate)
            ? currentDate.toDate()
            : newToDate.toDate(),
        };
      } else {
        const daysDiff = toDate.diff(fromDate, "days");
        const newFromDate = moment(toDate).add(1, "days");

        if (newFromDate.isAfter(currentDate)) {
          return prev;
        }

        const newToDate = moment(newFromDate).add(daysDiff, "days");
        return {
          from: newFromDate.toDate(),
          to: newToDate.isAfter(currentDate)
            ? currentDate.toDate()
            : newToDate.toDate(),
        };
      }
    });
  };

  // Move the date range backward by the current window size
  const handlePrevious = () => {
    // Reset Select All and checked columns when navigating
    setSelectAll(false);
    setCheckedColumns({});

    setDateRange((prev) => {
      const fromDate = moment(prev.from);
      const toDate = moment(prev.to);
      const currentDate = moment();

      const isFirstDay = fromDate.date() === 1;
      const isLastDay = toDate.isSame(moment(fromDate).endOf("month"), "day");

      if (isFirstDay && isLastDay) {
        const newFromDate = moment(fromDate)
          .subtract(1, "month")
          .startOf("month");
        const newToDate = moment(newFromDate).endOf("month");

        return {
          from: newFromDate.toDate(),
          to: newToDate.isAfter(currentDate)
            ? currentDate.toDate()
            : newToDate.toDate(),
        };
      } else {
        const daysDiff = toDate.diff(fromDate, "days");
        const newFromDate = moment(fromDate).subtract(daysDiff + 1, "days");
        const newToDate = moment(fromDate).subtract(1, "days");

        return {
          from: newFromDate.toDate(),
          to: newToDate.isAfter(currentDate)
            ? currentDate.toDate()
            : newToDate.toDate(),
        };
      }
    });
  };

  // Collect all timesheets from selected dates and filter by status
  const getTimesheetsForBulkAction = useCallback(
    (targetStatusId) => {
      const selectedDates = Object.keys(checkedColumns).filter(
        (date) => checkedColumns[date],
      );
      const timesheetIds = [];

      staffListTimesheet.forEach((staff) => {
        if (!staff.timesheets || staff.timesheets.length === 0) return;

        selectedDates.forEach((dateStr) => {
          const date = moment(dateStr).format("YYYY-MM-DD");
          const timesheetsForDate = staff.timesheets.filter((ts) => {
            const tsDate = moment(ts.date).format("YYYY-MM-DD");
            return tsDate === date && ts.statusId === targetStatusId;
          });

          timesheetsForDate.forEach((ts) => {
            timesheetIds.push(ts.id);
          });
        });
      });

      return timesheetIds;
    },
    [checkedColumns, staffListTimesheet],
  );

  // Check if action buttons should be enabled
  const hasPendingTimesheets = useMemo(() => {
    return (
      getTimesheetsForBulkAction(timesheetListingStatus[1]?.value).length > 0
    );
  }, [getTimesheetsForBulkAction, timesheetListingStatus]);

  const hasApprovedTimesheets = useMemo(() => {
    return (
      getTimesheetsForBulkAction(timesheetListingStatus[2]?.value).length > 0
    );
  }, [getTimesheetsForBulkAction, timesheetListingStatus]);

  // Bulk update status
  const handleBulkUpdateStatus = useCallback(
    async (statusId, reason) => {
      if (showLoader) return;
      setShowLoader(true);
      try {
        const response = await updateTimesheetStatus({
          timesheetId: selectedTimesheetIds.toString(),
          statusId,
          reason: reason || "",
        });
        toast.success(response?.message);
        refetch();
        setSelectedTimesheetIds([]);
      } catch (error) {
        toast.error(error?.message);
      } finally {
        setShowLoader(false);
      }
    },
    [selectedTimesheetIds, showLoader, refetch],
  );

  // Handle bulk approve
  const handleBulkApprove = () => {
    const ids = getTimesheetsForBulkAction(timesheetListingStatus[1]?.value);
    if (ids.length === 0) {
      toast.error("No pending timesheets found in selected dates");
      return;
    }
    setSelectedTimesheetIds(ids);
    setBulkConfirmModal({ open: true, type: "approve" });
  };

  // Handle bulk reject
  const handleBulkReject = () => {
    const ids = getTimesheetsForBulkAction(timesheetListingStatus[1]?.value);
    if (ids.length === 0) {
      toast.error("No pending timesheets found in selected dates");
      return;
    }
    setSelectedTimesheetIds(ids);
    setBulkRejectionModal(true);
  };

  // Handle bulk mark as paid
  const handleBulkMarkAsPaid = () => {
    const ids = getTimesheetsForBulkAction(timesheetListingStatus[2]?.value);
    if (ids.length === 0) {
      toast.error("No approved timesheets found in selected dates");
      return;
    }
    setSelectedTimesheetIds(ids);
    setBulkConfirmModal({ open: true, type: "paid" });
  };

  // Handle Apply Filters button click
  const handleApplyFilters = (filters) => {
    setSelectedUserIds(filters.userIds || []);
    setSelectedDesignationIds(filters.designationIds || []);
    setSelectedStaffStatus(
      Array.isArray(filters.staffStatus)
        ? filters.staffStatus
        : filters.staffStatus?.value
          ? [filters.staffStatus.value]
          : ["IsActiveInclude"],
    );
    setSelectedTimesheetStatuses(filters.timesheetStatuses || []);
  };

  // Handle office filter change - applies immediately and clears staff selection
  const handleOfficeFilterChange = (officeIds) => {
    setSelectedOfficeIds(officeIds);
    // Clear staff selection when offices change
    setSelectedUserIds([]);
  };

  // Filter timesheets by selected status (for export)
  // This matches the logic used in StaffAttendanceTable
  const filterTimesheetsByStatusForExport = useCallback(
    (timesheets) => {
      // If no filter is applied, return all timesheets
      if (
        !selectedTimesheetStatuses ||
        selectedTimesheetStatuses.length === 0
      ) {
        return timesheets;
      }

      if (!timesheets || timesheets.length === 0) {
        return timesheets;
      }

      // Map filter values to actual status values
      const statusValueMap = {
        pending: Number(timesheetListingStatus[1]?.value), // 2
        approved: Number(timesheetListingStatus[2]?.value), // 3
        rejected: Number(timesheetListingStatus[3]?.value), // 4
        paid: Number(timesheetListingStatus[4]?.value), // 5
      };

      // Get the status values for selected filters (excluding "mixed")
      const selectedStatusValues = selectedTimesheetStatuses
        .filter((status) => status !== "mixed")
        .map((status) => statusValueMap[status])
        .filter((val) => !isNaN(val) && val !== null && val !== undefined);

      // Check if "Mixed" is selected
      const isMixedSelected = selectedTimesheetStatuses.includes("mixed");

      // Get unique statusIds from timesheets
      const statusIds = timesheets
        .map((ts) => Number(ts.statusId))
        .filter((id) => !isNaN(id) && id !== null && id !== undefined);
      const uniqueStatusIds = [...new Set(statusIds)];
      const isMixed = uniqueStatusIds.length > 1;

      // If only "Mixed" is selected (no other statuses), only show timesheets if this has multiple unique statuses
      if (isMixedSelected && selectedStatusValues.length === 0) {
        return isMixed ? timesheets : [];
      }

      // If "Mixed" is selected along with other statuses
      if (isMixedSelected && selectedStatusValues.length > 0) {
        const hasMatchingStatus = uniqueStatusIds.some((statusId) =>
          selectedStatusValues.includes(statusId),
        );
        if (isMixed && hasMatchingStatus) {
          // If has mixed statuses and at least one matches, show all timesheets
          return timesheets;
        }
        // Otherwise, only show timesheets matching selected statuses
        return timesheets.filter((ts) => {
          const statusId = Number(ts.statusId);
          return selectedStatusValues.includes(statusId);
        });
      }

      // If only specific statuses are selected (no "Mixed"), filter by those statuses
      if (selectedStatusValues.length === 0) {
        return [];
      }

      // Filter timesheets to only include those matching ANY of the selected statuses
      return timesheets.filter((ts) => {
        const statusId = Number(ts.statusId);
        return selectedStatusValues.includes(statusId);
      });
    },
    [selectedTimesheetStatuses, timesheetListingStatus],
  );

  // Prepare filtered export payload
  // Uses staffListTimesheet (already filtered by status) and filters timesheets within each staff
  const prepareFilteredExportPayload = useCallback(() => {
    if (!data?.data) {
      return data?.data;
    }

    // If no timesheet status filter is applied, return all data (excluding logged-in user)
    if (!selectedTimesheetStatuses || selectedTimesheetStatuses.length === 0) {
      // Still filter out logged-in user for consistency
      const loggedInUserId = profile?.id;
      const filteredStaff = (data.data.staff || []).filter(
        (staff) => staff?.user?.id !== loggedInUserId,
      );
      return {
        ...data.data,
        staff: filteredStaff,
        holidays: data.data.holidays || [],
      };
    }

    // When filter is applied, use staffListTimesheet which is already filtered
    // If staffListTimesheet is empty, it means no staff match the criteria
    if (!staffListTimesheet || staffListTimesheet.length === 0) {
      return {
        ...data.data,
        staff: [],
        holidays: data.data.holidays || [],
      };
    }

    // Start with the filtered staff list (already filtered by timesheet status and search)
    // This ensures we only export staff that match the filter criteria
    const filteredStaff = staffListTimesheet
      .map((staff) => {
        // Filter timesheets within each staff member based on selected statuses
        const filteredTimesheets = filterTimesheetsByStatusForExport(
          staff.timesheets || [],
        );

        // Calculate total time spent from filtered timesheets (matching table calculation)
        const totalTimeSpent = filteredTimesheets.reduce(
          (total, ts) => total + (ts.timeSpent || 0),
          0,
        );

        // Return staff with filtered timesheets and calculated total
        return {
          ...staff,
          timesheets: filteredTimesheets,
          // Add totalTimeSpent to match what's displayed in the table
          totalTimeSpent: totalTimeSpent,
        };
      })
      // Remove staff members that have no timesheets after filtering
      .filter((staff) => {
        return staff.timesheets && staff.timesheets.length > 0;
      });

    // Return in the same format as data?.data
    return {
      ...data.data,
      staff: filteredStaff,
      // Keep holidays as they are
      holidays: data.data.holidays || [],
    };
  }, [
    staffListTimesheet,
    data,
    filterTimesheetsByStatusForExport,
    selectedTimesheetStatuses,
    profile?.id,
  ]);

  // Export timesheet data
  const downloadTimesheetExportedData = async () => {
    setShowLoader(true);

    try {
      // Use filtered data if timesheet status filter is applied, otherwise use full data
      const exportPayload = prepareFilteredExportPayload();

      let res = await exportGlobalTimesheetReport(
        startDate,
        endDate,
        exportPayload,
      );
      if (res?.data) {
        window.open(res?.data?.file_Url, "_self");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setShowLoader(false);
    }
  };

  const handleAddTimesheetForStaff = useCallback(
    (selectedStaff) => {
      const selectedOfficeId =
        selectedStaff?.office?.id || selectedStaff?.officeId;
      const selectedUserId = selectedStaff?.user?.id || selectedStaff?.userId;
      const normalizedStaffDetail = {
        ...selectedStaff,
        officeId: selectedOfficeId,
        userId: selectedUserId,
        firstName:
          selectedStaff?.firstName || selectedStaff?.user?.firstName || "",
        lastName:
          selectedStaff?.lastName || selectedStaff?.user?.lastName || "",
      };
      normalizedStaffDetail.name =
        normalizedStaffDetail.name ||
        `${normalizedStaffDetail.firstName} ${normalizedStaffDetail.lastName}`.trim();

      if (!selectedOfficeId || !selectedUserId) {
        return;
      }

      history.push({
        pathname: constants.routes.accountOwner.updateTimesheetForStaff
          .replace(":officeId", encodeId(selectedOfficeId))
          .replace(":staffId", encodeId(selectedUserId)),
        state: {
          staffDetail: normalizedStaffDetail,
          backPath: constants.routes.staffAttendance.staffAttendance,
        },
      });
    },
    [history],
  );

  return (
    <Page
      onBack={() => history.push(constants.routes.accountOwner.offices)}
      className="scheduler-page"
    >
      <div
        className={
          styles.title_btn_container +
          (isStaff && commonOwnerData?.id ? "" : " marginBottom30")
        }
      >
        <p className="title">{t("staffAttendance.staffAttendance")}</p>
        <div className={styles.help_button_container}>
          {canAccessThisPage && (
            <Button
              borderedSecondary
              onClick={downloadTimesheetExportedData}
              className={styles.export_btn}
            >
              {t("staffAttendance.exportTimesheetData")}
            </Button>
          )}
          {canAccessThisPage && canAddTimesheet && (
            <Button
              onClick={() => setIsAddTimesheetModalOpen(true)}
              fontWeight="700"
              boxShadow="0 4px 12px rgba(45, 50, 69, 0.18)"
            >
              {t("staffTimesheet.addEditTimesheet")}
            </Button>
          )}
        </div>
      </div>

      {(isLoading ||
        isFetching ||
        showLoader ||
        getCommonAccessLoading ||
        ownerListLoading) && <Loader />}
      {isStaff && commonOwnerData?.id && (
        <div className="marginBottom30 show-owner-name-text">
          {t("staffAttendance.showingStaffAttendanceFor")}{" "}
          <span>
            '{commonOwnerData.firstName || ""} {commonOwnerData.lastName || ""}'
          </span>
          {t("dashBoard.office") || ""}
        </div>
      )}
      {canAccessThisPage ? (
        <Card>
          <div className="scheduler-tabs-main-wrapper ">
            <StaffAttendanceSidebar
              t={t}
              search={search}
              onSearch={handleSearch}
              dateRange={dateRange}
              updateDateRange={updateDateRange}
              holidays={holidays}
              selectedOfficeIds={selectedOfficeIds}
              setSelectedOfficeIds={handleOfficeFilterChange}
              selectedUserIds={selectedUserIds}
              setSelectedUserIds={setSelectedUserIds}
              selectedDesignationIds={selectedDesignationIds}
              setSelectedDesignationIds={setSelectedDesignationIds}
              selectedStaffStatus={selectedStaffStatus}
              setSelectedStaffStatus={setSelectedStaffStatus}
              selectedTimesheetStatuses={selectedTimesheetStatuses}
              setSelectedTimesheetStatuses={setSelectedTimesheetStatuses}
              ownerId={selectedId}
              timesheetListingStatus={timesheetListingStatus}
              onApplyFilters={handleApplyFilters}
            />
            <Card
              className={styles.overflow_x_auto + " w-100 shadow-none pr-0"}
            >
              <div className="tabs-right-col appointment-tab-right-col h-100 px-0">
                <div className={styles.legends_sort_container}>
                  <div className={styles.legend}>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.current_day}`}
                      ></span>{" "}
                      {t("staffAttendance.currentDay")}
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.holiday_legent}`}
                      ></span>{" "}
                      {t("staffAttendance.holiday")}
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.leave}`}
                      ></span>{" "}
                      {t("staffAttendance.leave")}
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.pending_for_approval}`}
                      ></span>{" "}
                      {t("staffAttendance.pendingForApproval")}
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.approved}`}
                      ></span>{" "}
                      {t("staffAttendance.approved")}
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.rejected}`}
                      ></span>{" "}
                      {t("staffAttendance.rejected")}
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.paid}`}
                      ></span>{" "}
                      {t("staffAttendance.paid")}
                    </div>
                    <div className={styles.legendItem}>
                      <span
                        className={`${styles.colorBox} ${styles.mixed}`}
                      ></span>{" "}
                      {t("staffAttendance.mixed")}
                    </div>
                  </div>
                  <div className={styles.sorting_container}>
                    <CustomSortDropdown
                      setSortBy={setSelectedSort}
                      sortBy={selectedSort}
                      sortByList={sortByList}
                    />
                    {showGoToCurrentDate && (
                      <button
                        className="link-btn bg-transparent outline-0 outline-none border-0"
                        onClick={goToCurrentDate}
                      >
                        {t("staffAttendance.goToCurrentDate")}
                      </button>
                    )}
                    <Button borderedSecondary onClick={handlePrevious}>
                      {t("staffAttendance.previous")}
                    </Button>
                    <Button
                      borderedSecondary
                      onClick={handleNext}
                      disabled={isAtCurrentPeriodEnd}
                      title={
                        isAtCurrentPeriodEnd
                          ? t("staffAttendance.youAreOnTheCurrentDate")
                          : undefined
                      }
                    >
                      {t("staffAttendance.next")}
                    </Button>
                  </div>
                </div>

                <div className={styles.select_all_container}>
                  <div className="ch-checkbox">
                    <label className="m-0">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                      />
                      <span>{t("staffAttendance.selectAll")}</span>
                    </label>
                  </div>

                  <button
                    className="link-btn"
                    onClick={handleBulkApprove}
                    disabled={!hasPendingTimesheets}
                  >
                    {t("staffAttendance.approve")}
                  </button>
                  <button
                    className="link-btn"
                    onClick={handleBulkReject}
                    disabled={!hasPendingTimesheets}
                  >
                    {t("staffAttendance.reject")}
                  </button>
                  <button
                    className="link-btn"
                    onClick={handleBulkMarkAsPaid}
                    disabled={!hasApprovedTimesheets}
                  >
                    {t("staffAttendance.markAsPaid")}
                  </button>
                </div>

                <StaffAttendanceTable
                  checkedColumns={checkedColumns}
                  onCheckboxChange={handleCheckboxChange}
                  dateHeaders={dateHeaders}
                  staffListTimesheet={staffListTimesheet}
                  holidays={holidays}
                  startDate={startDate}
                  endDate={endDate}
                  t={t}
                  refetch={refetch}
                  setShowLoader={setShowLoader}
                  showLoader={showLoader}
                  selectedTimesheetStatuses={selectedTimesheetStatuses}
                  timesheetListingStatus={timesheetListingStatus}
                />
              </div>
            </Card>
          </div>
        </Card>
      ) : (
        <div
          className="not-accessible center_property"
          style={{ height: "300px" }}
        >
          <NotAccessible Message={t("dashBoard.notAccessible")} />
        </div>
      )}
      {bulkConfirmModal.open && (
        <FamilyModal
          isFamilyModalOpen={bulkConfirmModal.open}
          setIsFamilyModalOpen={(val) =>
            setBulkConfirmModal((prev) => ({ ...prev, open: val }))
          }
          title={
            bulkConfirmModal.type === "approve"
              ? t("staffAttendance.approveTimesheet")
              : t("staffAttendance.markTimesheetAsPaid")
          }
          subTitle1={
            bulkConfirmModal.type === "approve"
              ? t(
                  "staffAttendance.onlyTimesheetsWithPendingStatusWillBeApproved",
                )
              : t(
                  "staffAttendance.onlyTimesheetsWithAcceptedStatusWillBeMarkedAsPaid",
                )
          }
          leftBtnText={
            bulkConfirmModal.type === "approve"
              ? t("staffAttendance.approve")
              : t("staffAttendance.markAsPaid")
          }
          rightBtnText={t("cancel")}
          onConfirm={async () => {
            const statusId =
              bulkConfirmModal.type === "approve"
                ? timesheetListingStatus[2]?.value
                : timesheetListingStatus[4]?.value;
            setBulkConfirmModal({ open: false, type: null });
            await handleBulkUpdateStatus(statusId);
          }}
        />
      )}
      {bulkRejectionModal && (
        <RejectionModal
          isRejectionModalOpen={bulkRejectionModal}
          setIsRejectionModalOpen={setBulkRejectionModal}
          staffMemberName=""
          hideSelectedEmployee={true}
          titleOverride={t("staffAttendance.rejectTimesheet")}
          confirmLabelOverride={t("staffAttendance.reject")}
          confirmMessage={t(
            "staffAttendance.areYouSureYouWantToRejectTheTimesheet",
          )}
          leadMessage={t(
            "staffAttendance.onlyTimesheetsWithPendingStatusWillBeRejected",
          )}
          centerTitle={true}
          onReject={async (reason) => {
            setBulkRejectionModal(false);
            await handleBulkUpdateStatus(
              timesheetListingStatus[3]?.value,
              reason,
            );
          }}
        />
      )}
      {isAddTimesheetModalOpen && (
        <AddTimesheetStaffModal
          isOpen={isAddTimesheetModalOpen}
          ownerId={selectedId}
          onClose={() => setIsAddTimesheetModalOpen(false)}
          onSelectStaff={handleAddTimesheetForStaff}
        />
      )}
    </Page>
  );
};

export default withTranslation()(StaffAttendance);
